import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  HStack,
} from '@chakra-ui/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const ChangesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [changes, setChanges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchChanges();
  }, []);

  const fetchChanges = async () => {
    try {
      const dataset_id = localStorage.getItem('dataset_id');
      const response = await axios.post(`${API_URL}/api/scripts/cache/list`, {
        dataset_id: dataset_id
      });
      setChanges(response.data.states || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch changes history',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevert = async (cacheKey) => {
    try {
      const dataset_id = localStorage.getItem('dataset_id');
      
      // Call the clear-after endpoint
      await axios.post(`${API_URL}/api/scripts/cache/clear-after`, {
        dataset_id: dataset_id,
        cache_key: cacheKey
      });

      // Update the importedDataFrame in localStorage with the new cache key
      const importedDataFrame = JSON.parse(localStorage.getItem('importedDataFrame') || '{}');
      importedDataFrame.cache_key = cacheKey;
      localStorage.setItem('importedDataFrame', JSON.stringify(importedDataFrame));
      
      toast({
        title: 'Success',
        description: 'Changes reverted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reload the changes list
      await fetchChanges();
      
      // Navigate back to dashboard
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to revert changes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatColumns = (columns) => {
    if (!columns) return '';
    return Array.isArray(columns) ? columns.join(', ') : columns;
  };

  const formatMetadataValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderSampleData = (sampleData, columns) => {
    if (!sampleData || !columns) return null;
    
    try {
      const data = typeof sampleData === 'string' ? JSON.parse(sampleData) : sampleData;
      return (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                {columns.map((column) => (
                  <Th key={column}>{column}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {data.map((row, rowIndex) => (
                <Tr key={rowIndex}>
                  {columns.map((column) => (
                    <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      );
    } catch (error) {
      console.error('Error parsing sample data:', error);
      return <Text color="red.500">Error displaying sample data</Text>;
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box p={6} borderRadius="lg" boxShadow="md">
        <HStack justify="space-between" align="center" mb={6}>
          <Heading>Cambios</Heading>
          <Button onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </Button>
        </HStack>

        {isLoading ? (
          <Box textAlign="center" py={8}>
            <Spinner size="xl" />
            <Text mt={4}>Cargando cambios...</Text>
          </Box>
        ) : changes.length === 0 ? (
          <Text textAlign="center" py={8}>
            No hay cambios registrados
          </Text>
        ) : (
          <Accordion allowMultiple>
            {changes.map((change, index) => (
              <AccordionItem key={index}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Badge colorScheme="blue">{change.metadata.type || 'Unknown Script'}</Badge>
                        <Text>{formatDate(change.timestamp)}</Text>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Script:</Text>
                      <Text>{change.metadata.type || 'Unknown Script'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Columns:</Text>
                      <Text>{formatColumns(change.metadata.columns)}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Metadata:</Text>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Key</Th>
                            <Th>Value</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {Object.entries(change.metadata)
                            .filter(([key]) => key !== 'sample_data')
                            .map(([key, value]) => (
                              <Tr key={key}>
                                <Td>{key}</Td>
                                <Td>{formatMetadataValue(value)}</Td>
                              </Tr>
                            ))}
                        </Tbody>
                      </Table>
                    </Box>
                    {change.metadata.sample_data && (
                      <Box>
                        <Text fontWeight="bold" mb={2}>Sample Data (First 5 rows):</Text>
                        {renderSampleData(change.metadata.sample_data, change.metadata.columns)}
                      </Box>
                    )}
                    <Button 
                      colorScheme="blue" 
                      onClick={() => handleRevert(change.key)}
                      mt={4}
                    >
                      Revertir a este estado
                    </Button>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </Box>
    </Container>
  );
};

export default ChangesPage; 